/**
 * DXF HEADER persistence for AutoCAD annotative system variables (PR-2).
 *
 * AutoCAD writes each annotative-related sysvar into the HEADER section
 * as an alternating `9 $NAME` + `<code> <value>` pair. The four vars
 * relevant to annotation-scale parity are:
 *
 *   $CANNOSCALE       — string (group 1)  — current annotation scale name
 *   $CANNOSCALEVALUE  — float  (group 40) — corresponding scale factor
 *   $ANNOALLVISIBLE   — bool   (group 290) — show annotations at all scales
 *   $ANNOAUTOSCALE    — int    (group 70)  — auto-add new scale to entities
 *   $ANNOMONITOR      — bool   (group 290) — highlight out-of-sync entities
 *
 * Mirrors the design of `headerDynVars.ts` which handles the
 * `$DYNMODE`/`$DYNPICOORDS`/... family. The two modules are kept
 * separate because the value codes differ (DYN vars are all int16,
 * annotative vars use 1/40/70/290) and the consumer surface (bridge
 * import vs DI subsystem) is unrelated.
 *
 * AutoCAD reference: drawing system-variable reference 2018+,
 * "Annotation Scale Variables" section.
 */

import { decodeDxfUnicode } from "./dxfUnicodeEscape";
import type { DxfGroup } from "./types";

// ── Public types ─────────────────────────────────────────────────────────

/**
 * Decoded values for the annotative HEADER variables. Every field is
 * optional so a legacy DXF without annotative sysvars yields an empty
 * object — the bridge layer applies the schema defaults from
 * `model.ts` (`cannoscaleName: "1:1"`, `annoAllVisible: true`,
 * `annoAutoScale: 4`, `annoMonitor: false`).
 */
export type HeaderAnnotativeVars = {
  /** $CANNOSCALE — group 1 — current annotation scale name. */
  cannoscaleName?: string;
  /** $CANNOSCALEVALUE — group 40 — current annotation scale factor. */
  cannoscaleValue?: number;
  /** $ANNOALLVISIBLE — group 290 — show all annotations regardless of scale. */
  annoAllVisible?: boolean;
  /** $ANNOAUTOSCALE — group 70 — -4..4 spec value (positive = enabled). */
  annoAutoScale?: number;
  /** $ANNOMONITOR — group 290 — highlight out-of-sync annotative entities. */
  annoMonitor?: boolean;
};

const VAR_NAMES = [
  "$CANNOSCALE",
  "$CANNOSCALEVALUE",
  "$ANNOALLVISIBLE",
  "$ANNOAUTOSCALE",
  "$ANNOMONITOR",
] as const;

const VAR_NAME_SET = new Set<string>(VAR_NAMES);

// ── Read ─────────────────────────────────────────────────────────────────

/**
 * Scan a HEADER section's flat group list and extract the annotative
 * sysvars. Groups are expected in the AutoCAD pattern:
 *
 *   { code: 9, value: "$NAME" }
 *   { code: <typed>, value: "..." }
 *
 * Unknown variables (not in `VAR_NAMES`) are ignored. Variables with a
 * name match but an unexpected value code are also ignored — keeps the
 * reader robust against AutoCAD format updates.
 */
export function readAnnotativeVarsFromHeader(
  headerGroups: ReadonlyArray<DxfGroup>,
): HeaderAnnotativeVars {
  const result: HeaderAnnotativeVars = {};

  for (let i = 0; i < headerGroups.length - 1; i++) {
    const nameGroup = headerGroups[i];
    const valueGroup = headerGroups[i + 1];
    if (!nameGroup || !valueGroup) continue;
    if (nameGroup.code !== 9) continue;
    const name = nameGroup.value.trim();
    if (!VAR_NAME_SET.has(name)) continue;

    switch (name) {
      case "$CANNOSCALE":
        if (valueGroup.code === 1) {
          // T9 PR-3 — the writer chokepoint `\U+`-escapes this HEADER string
          // group (code 1) on AC1015; decode so a Cyrillic current-annotation-
          // scale name re-imports as the real characters and keeps matching the
          // decoded scale catalogue. Symmetric with the re-escape on write.
          result.cannoscaleName = decodeDxfUnicode(valueGroup.value);
          i++;
        }
        break;
      case "$CANNOSCALEVALUE":
        if (valueGroup.code === 40) {
          const n = Number.parseFloat(valueGroup.value);
          if (Number.isFinite(n)) result.cannoscaleValue = n;
          i++;
        }
        break;
      case "$ANNOALLVISIBLE":
        if (valueGroup.code === 290) {
          result.annoAllVisible = Number.parseInt(valueGroup.value, 10) !== 0;
          i++;
        }
        break;
      case "$ANNOAUTOSCALE":
        if (valueGroup.code === 70) {
          const n = Number.parseInt(valueGroup.value, 10);
          if (Number.isFinite(n)) result.annoAutoScale = n;
          i++;
        }
        break;
      case "$ANNOMONITOR":
        if (valueGroup.code === 290) {
          result.annoMonitor = Number.parseInt(valueGroup.value, 10) !== 0;
          i++;
        }
        break;
    }
  }

  return result;
}

// ── Write ────────────────────────────────────────────────────────────────

/**
 * Produce the DxfGroup pairs for the annotative HEADER variables.
 * Symmetric with `readAnnotativeVarsFromHeader` — round-trip safe.
 *
 * Only fields present on the input are emitted. The writer in PR-5
 * uses this to splice the values into an existing HEADER section.
 */
export function writeAnnotativeVarsToHeaderLines(
  vars: HeaderAnnotativeVars,
): DxfGroup[] {
  const out: DxfGroup[] = [];
  if (vars.cannoscaleName !== undefined) {
    out.push({ code: 9, value: "$CANNOSCALE" });
    out.push({ code: 1, value: vars.cannoscaleName });
  }
  if (vars.cannoscaleValue !== undefined) {
    out.push({ code: 9, value: "$CANNOSCALEVALUE" });
    out.push({ code: 40, value: String(vars.cannoscaleValue) });
  }
  if (vars.annoAllVisible !== undefined) {
    out.push({ code: 9, value: "$ANNOALLVISIBLE" });
    out.push({ code: 290, value: vars.annoAllVisible ? "1" : "0" });
  }
  if (vars.annoAutoScale !== undefined) {
    out.push({ code: 9, value: "$ANNOAUTOSCALE" });
    out.push({ code: 70, value: String(vars.annoAutoScale) });
  }
  if (vars.annoMonitor !== undefined) {
    out.push({ code: 9, value: "$ANNOMONITOR" });
    out.push({ code: 290, value: vars.annoMonitor ? "1" : "0" });
  }
  return out;
}
